import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useCountdown } from '@/hooks/useCountdown';
import { downloadICalFile, getGoogleCalendarUrl } from '@/utils/calendarExport';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export const ScheduledStreamsCard = () => {
  const navigate = useNavigate();
  const [scheduledStreams, setScheduledStreams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScheduledStreams();
  }, []);

  const loadScheduledStreams = async () => {
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*, profiles(display_name, avatar_url)')
        .eq('status', 'scheduled')
        .gte('started_at', new Date().toISOString())
        .order('started_at', { ascending: true })
        .limit(5);

      if (error) throw error;
      setScheduledStreams(data || []);
    } catch (error) {
      console.error('Error loading scheduled streams:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (scheduledStreams.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No scheduled streams yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const CountdownTimer = ({ startDate }: { startDate: string }) => {
    const timeLeft = useCountdown(startDate);

    if (timeLeft.total <= 0) return <span className="text-xs text-muted-foreground">Starting soon</span>;

    return (
      <div className="flex items-center gap-2 text-xs font-medium">
        {timeLeft.days > 0 && <span className="px-2 py-1 bg-primary/10 text-primary rounded">{timeLeft.days}d</span>}
        {(timeLeft.days > 0 || timeLeft.hours > 0) && <span className="px-2 py-1 bg-primary/10 text-primary rounded">{timeLeft.hours}h</span>}
        {timeLeft.days === 0 && <span className="px-2 py-1 bg-primary/10 text-primary rounded">{timeLeft.minutes}m</span>}
        {timeLeft.days === 0 && timeLeft.hours === 0 && <span className="px-2 py-1 bg-primary/10 text-primary rounded">{timeLeft.seconds}s</span>}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Upcoming Streams
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {scheduledStreams.map((stream) => (
          <div
            key={stream.id}
            className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div 
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => navigate(`/stream/${stream.id}`)}
              >
                <p className="font-medium text-sm truncate">{stream.title}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {stream.profiles?.display_name}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {stream.started_at ? format(new Date(stream.started_at), 'MMM d, h:mm a') : 'TBA'}
                  </div>
                </div>
                {stream.started_at && (
                  <div className="mt-2">
                    <CountdownTimer startDate={stream.started_at} />
                  </div>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    window.open(getGoogleCalendarUrl({
                      ...stream,
                      description: stream.description || undefined
                    }), '_blank');
                  }}>
                    Add to Google Calendar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    downloadICalFile({
                      ...stream,
                      creator_name: stream.profiles?.display_name
                    });
                  }}>
                    Download iCal File
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
